import React from 'react';
import { Box, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';

import Bloco from './componentes/Bloco';
import Colunas from './componentes/Colunas';
import TituloDaTela from './componentes/TituloDaTela';
import NumeroProtagonista from './componentes/NumeroProtagonista';
import ListaDeIndicadores from './componentes/ListaDeIndicadores';
import BarraDeComposicao from './componentes/BarraDeComposicao';
import Sparkline from './componentes/Sparkline';
import LineChartCard from '../components/LineChartCard';
import CoberturaChip from '../components/CoberturaChip';
import { SECOES } from '../diagnostico/catalogo';
import {
  montarSecao,
  resumoDosResultados,
  composicaoDoFaturamento,
  leituraDoPontoDeEquilibrio,
  ressalvaDaTendencia,
  formatarMoedaCurta,
  formatarPorTipo
} from './apresentacao';
import { coresDoGrafico } from '../theme/modernTheme';
import { formatarPercentual } from '../utils/series';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Tela Resultado — e ela exibe OS DOIS resultados.
 *
 * Decisão do dono, tomada depois que a onda 0 provou que a planilha do cliente
 * se contradiz: o "Lucro Real" dela deixa a compra de mercadoria de fora
 * (correto) e nunca soma o CMV de volta, enquanto o ponto de equilíbrio dela
 * usa o CMV. No fixture, a empresa "lucra" R$ 3.214 faturando R$ 9.689 num mês
 * cujo ponto de equilíbrio é R$ 16.264 — dois números que não podem ser
 * verdade ao mesmo tempo.
 *
 * A tela mostra os dois, com o CMV explícito entre eles. Corrigir em silêncio
 * faria o dashboard divergir do arquivo do cliente, e é a reconciliação com
 * esse arquivo que dá ao consultor a capacidade de provar o número na frente
 * dele.
 */

// Já aparecem na escada acima: repetir na lista de apoio criaria dois nomes
// para o mesmo número, que é como este produto acabou com quatro pares assim.
const JA_EXIBIDOS = ['receitaLiquida', 'margemLiquida', 'margemLiquidaCompetencia'];

/**
 * Margem Bruta desaparece quando ela É a Margem de Contribuição.
 *
 * As duas fórmulas são diferentes de verdade — a de contribuição desconta ainda
 * comissão e taxa de cartão —, mas para quem não informou custo variável de
 * venda elas dão o MESMO número. A tela exibia "Margem Bruta 58,5%" e, na linha
 * seguinte, "Margem de Contribuição 58,5% · Excelente": a mesma medida com dois
 * nomes, uma com faixa e outra sem, na mesma dobra.
 *
 * Some a que não tem faixa, porque é a que diz menos. Quando as duas divergem,
 * as duas aparecem: aí elas são leituras diferentes e a diferença é informação.
 */
const semMargemBrutaRedundante = (itens) => {
  const bruta = itens.find((i) => i.chave === 'margemBruta');
  const contribuicao = itens.find((i) => i.chave === 'margemContribuicao');
  if (!bruta || !contribuicao) return itens;
  if (bruta.valor === null || contribuicao.valor === null) return itens;
  // Um décimo de ponto percentual: abaixo disso as duas se escrevem igual na
  // tela, e exibir as duas só faz o leitor procurar a diferença que não existe.
  if (Math.abs(bruta.valor - contribuicao.valor) >= 0.1) return itens;
  return itens.filter((i) => i.chave !== 'margemBruta');
};

const LinhaDaEscada = ({ rotulo, valor, sinal, forte, ajuda }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
    <Typography variant={forte ? 'subtitle1' : 'body2'} color={forte ? 'text.primary' : 'text.secondary'}>
      {sinal ? `${sinal} ` : ''}
      {rotulo}
      {ajuda && (
        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
          {ajuda}
        </Typography>
      )}
    </Typography>
    <Typography
      className="valor-numerico"
      variant={forte ? 'subtitle1' : 'body2'}
      sx={{ fontWeight: forte ? 600 : 500, whiteSpace: 'nowrap' }}
    >
      {formatarMoedaCurta(valor)}
    </Typography>
  </Stack>
);

const TelaResultado = ({ leitura }) => {
  const tema = useTheme();
  const CORES_GRAFICO = coresDoGrafico(tema.palette.mode === 'dark');
  const resumo = resumoDosResultados(leitura);
  const unico = resumo.unico === 'caixa' ? 'caixa' : 'competencia';
  const margemDoUnico = (unico === 'caixa' ? resumo.caixa : resumo.competencia).margem;
  const leituraDoUnico = margemDoUnico === null
    ? null
    : `Margem de ${formatarPercentual(margemDoUnico, 1)} — ${unico === 'caixa' ? 'reconcilia com a sua planilha.' : 'já é a leitura em competência.'}`;
  const composicao = composicaoDoFaturamento(leitura);
  const apoio = semMargemBrutaRedundante(montarSecao(leitura, SECOES.RESULTADO, JA_EXIBIDOS));
  const custos = montarSecao(leitura, SECOES.ESTRUTURA_CUSTOS);
  const ressalva = ressalvaDaTendencia(leitura);
  const frasePE = leituraDoPontoDeEquilibrio(resumo);
  const rotulos = leitura?.rotulos || [];
  const series = leitura?.series || {};

  const dadosDaEvolucao = rotulos.map((month, i) => ({
    month,
    faturamento: series.faturamento?.[i] ?? null,
    pontoEquilibrio: series.pontoEquilibrio?.[i] ?? null
  }));

  const dadosDosResultados = rotulos.map((month, i) => ({
    month,
    caixa: series.lucroCaixa?.[i] ?? null,
    competencia: series.lucroCompetencia?.[i] ?? null
  }));

  // DUAS LINHAS IDÊNTICAS SOBREPOSTAS NÃO SÃO DUAS LINHAS.
  //
  // Quando a mercadoria já está no custo do mês, as duas séries são iguais em
  // todo ponto: o gráfico desenhava uma sobre a outra e a legenda prometia uma
  // comparação que o desenho não tinha. Aí ele vira o que é — um resultado.
  const doisResultadosDivergem = dadosDosResultados.some(
    (p) => p.caixa !== null && p.competencia !== null && Math.abs(p.caixa - p.competencia) >= 1
  );
  // A COR DA SÉRIE SEGUE O SINAL DELA, e não a posição na legenda.
  //
  // "Resultado com CMV" era verde fixo. No mês exibido ele vale −R$ 3.036 e o
  // cartão logo acima diz Crítico: a tela pintava de verde, ao lado de um
  // prejuízo, a linha que representa o prejuízo. Verde e vermelho aqui só
  // entram quando significam resultado bom ou ruim — é a regra escrita no
  // próprio tema, e ela vale para a série tanto quanto para o chip.
  const corDoResultado = (chave) => {
    const ultimos = dadosDosResultados.map((p) => p[chave]).filter((v) => v !== null && v !== undefined);
    if (!ultimos.length) return CORES_GRAFICO.secundaria;
    return ultimos[ultimos.length - 1] >= 0 ? CORES_GRAFICO.positiva : CORES_GRAFICO.negativa;
  };
  const linhasDoResultado = doisResultadosDivergem
    ? [
        { dataKey: 'caixa', color: corDoResultado('caixa'), name: 'Resultado de caixa' },
        { dataKey: 'competencia', color: corDoResultado('competencia'), name: 'Resultado com CMV' }
      ]
    : [{ dataKey: 'competencia', color: corDoResultado('competencia'), name: 'Resultado do mês' }];

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Resultado"
        descricao="O negócio dá lucro, e a partir de quanto de venda ele para de dar prejuízo?"
      />
      <Bloco
        titulo={resumo.mes ? `Resultado de ${resumo.mes}` : 'Resultado'}
        pergunta="O negócio dá lucro, e a partir de quanto de venda ele para de dar prejuízo?"
        acao={<CoberturaChip cobertura={leitura?.cobertura} />}
        nota={
          resumo.doisResultados
            ? 'O resultado de caixa é o número que a sua planilha mostra: ele desconta os custos do mês e deixa de fora a compra de mercadoria, que é desembolso de estoque. O resultado com CMV desconta o custo da mercadoria que de fato saiu — é ele que conversa com o ponto de equilíbrio.'
            : 'Neste lançamento existe UM resultado: a mercadoria já entrou como custo do mês, então ele já está em competência e é o que conversa com o ponto de equilíbrio. A escada de dois resultados aparece quando o lançamento vem de planilha, que separa o desembolso de estoque do custo da mercadoria vendida.'
        }
      >
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Sparkline
              serie={series.faturamento || []}
              rotulos={rotulos}
              rotulo="Faturamento"
              cor={CORES_GRAFICO.primaria}
              formatar={formatarMoedaCurta}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Sparkline
              serie={series.margemContribuicao || []}
              rotulos={rotulos}
              rotulo="Margem de contribuição"
              cor={CORES_GRAFICO.terciaria}
              formatar={(v) => formatarPercentual(v, 1)}
            />
          </Grid>
          {ressalva && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {ressalva}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Stack spacing={1} sx={{ mb: 3 }}>
          <LinhaDaEscada rotulo="Faturamento" valor={resumo.faturamento} />
          <LinhaDaEscada rotulo="Deduções sobre a venda" valor={resumo.deducoes} sinal="−" />
          <LinhaDaEscada rotulo="Receita líquida" valor={resumo.receitaLiquida} forte />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* UM RESULTADO OU DOIS — e a tela não pode inventar o segundo.

            Quando a mercadoria já está dentro do custo do mês (é o que o
            questionário guiado produz), o motor não desconta o CMV de novo, e os
            dois resultados são o MESMO número. A tela ainda assim desenhava a
            escada inteira: "RESULTADO DE CAIXA R$ 12.100", "(−) Custo da
            mercadoria vendida R$ 48.000", "RESULTADO COM CMV R$ 12.100" — os
            dois com margem de 10,5%, e no meio uma subtração de quarenta e oito
            mil reais que não aconteceu. */}
        {!resumo.doisResultados ? (
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              {/* `resumo.unico` decide qual dos dois resultados é o único que
                  existe medido. Sem CMV informado, o resultado em competência
                  não pode ser afirmado e este card exibia um travessão no lugar
                  do número protagonista — a tela Resultado sem resultado. */}
              <NumeroProtagonista
                rotulo={unico === 'caixa' ? 'Resultado de caixa' : 'Resultado do mês'}
                valor={formatarMoedaCurta(unico === 'caixa' ? resumo.caixa.valor : resumo.competencia.valor)}
                nivel={(unico === 'caixa' ? resumo.caixa : resumo.competencia).faixa.nivel}
                rotuloDaFaixa={(unico === 'caixa' ? resumo.caixa : resumo.competencia).faixa.rotulo}
                cor={(unico === 'caixa' ? resumo.caixa : resumo.competencia).faixa.cor}
                fonte={(unico === 'caixa' ? resumo.caixa : resumo.competencia).faixa.fonte}
                leitura={leituraDoUnico}
                ajuda={
                  unico === 'caixa'
                    ? 'Faturamento menos os custos que a planilha marca como custo do mês. A compra de mercadoria fica de fora.'
                    : 'Faturamento menos todos os custos do mês, mercadoria incluída.'
                }
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ pt: { md: 3 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '42ch' }}>
                  {resumo.cmvDentroDoCusto
                    ? 'A mercadoria do mês já está dentro do custo acima, então não há uma segunda subtração a fazer.'
                    : unico === 'caixa'
                      ? 'Este é o resultado que a sua planilha mostra: ele desconta os custos do mês e deixa de fora a compra de mercadoria. Sem o custo da mercadoria vendida informado, o resultado em competência — o que conversa com o ponto de equilíbrio — não pode ser calculado.'
                      : 'O custo da mercadoria vendida não foi informado neste lançamento, então não há uma segunda subtração a mostrar.'}
                </Typography>
                {resumo.cmv !== null && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                    Custo da mercadoria vendida no mês: {formatarMoedaCurta(resumo.cmv)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <NumeroProtagonista
                rotulo="Resultado de caixa"
                valor={formatarMoedaCurta(resumo.caixa.valor)}
                nivel={resumo.caixa.faixa.nivel}
                rotuloDaFaixa={resumo.caixa.faixa.rotulo}
                cor={resumo.caixa.faixa.cor}
                fonte={resumo.caixa.faixa.fonte}
                leitura={
                  resumo.caixa.margem === null
                    ? null
                    : `Margem de ${formatarPercentual(resumo.caixa.margem, 1)} — reconcilia com a sua planilha.`
                }
                ajuda="Faturamento menos os custos que a planilha marca como custo do mês. A compra de mercadoria fica de fora."
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: { xs: 'left', md: 'center' }, pt: { md: 3 } }}>
                <Typography variant="caption" color="text.secondary" component="p">
                  (−) Custo da mercadoria vendida
                </Typography>
                <Typography className="valor-numerico" variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {formatarMoedaCurta(resumo.cmv)}
                </Typography>
                {resumo.compraMercadoria !== null && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                    Comprou {formatarMoedaCurta(resumo.compraMercadoria)} de mercadoria no mês
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <NumeroProtagonista
                rotulo="Resultado com CMV"
                valor={formatarMoedaCurta(resumo.competencia.valor)}
                nivel={resumo.competencia.faixa.nivel}
                rotuloDaFaixa={resumo.competencia.faixa.rotulo}
                cor={resumo.competencia.faixa.cor}
                fonte={resumo.competencia.faixa.fonte}
                leitura={
                  resumo.competencia.margem === null
                    ? null
                    : `Margem de ${formatarPercentual(resumo.competencia.margem, 1)} — é a leitura em competência.`
                }
                ajuda="O mesmo resultado, descontando o custo da mercadoria que saiu do estoque no período."
              />
            </Grid>
          </Grid>
        )}

        {frasePE && (
          <Typography variant="body1" sx={{ mt: 3, maxWidth: '65ch' }}>
            {frasePE}
          </Typography>
        )}

        {apoio.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <ListaDeIndicadores itens={apoio} />
          </Box>
        )}
      </Bloco>

      <Bloco
        titulo="Para onde vai cada real"
        nota={
          composicao?.cmvDentroDoCusto
            ? 'Neste lançamento a mercadoria já está dentro do custo do mês — por isso ela não aparece como parcela separada. As duas parcelas fecham o faturamento.'
            : 'As parcelas fecham o faturamento: é a mesma subtração que produz o resultado em competência.'
        }
        pergunta={
          composicao?.mes
            ? `Composição do faturamento de ${composicao.mes}.`
            : 'Composição do faturamento do período.'
        }
      >
        {composicao ? (
          <BarraDeComposicao composicao={composicao} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            A composição exige faturamento, CMV e custo operacional no mesmo mês. Preencher uma das
            parcelas com zero afirmaria que aquele custo não existe.
          </Typography>
        )}

        <Box sx={{ mt: 3 }}>
          <ListaDeIndicadores itens={custos} />
        </Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Percentuais calculados sobre a receita líquida. Retirada de sócios não entra em folha e
          encargos — são leituras diferentes e somá-las acusaria de inchada toda empresa familiar.
        </Typography>
      </Bloco>

      <Colunas>
        <LineChartCard
          title="Faturamento e ponto de equilíbrio"
          data={dadosDaEvolucao}
          lines={[
            { dataKey: 'faturamento', color: CORES_GRAFICO.primaria, name: 'Faturamento' },
            { dataKey: 'pontoEquilibrio', color: CORES_GRAFICO.atencao, name: 'Ponto de equilíbrio' }
          ]}
        />
        <LineChartCard
          title={doisResultadosDivergem ? 'Os dois resultados, mês a mês' : 'O resultado, mês a mês'}
          data={dadosDosResultados}
          lines={linhasDoResultado}
        />
      </Colunas>

      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
        Ponto de equilíbrio no período mais recente:{' '}
        {formatarPorTipo(resumo.pontoEquilibrio, 'moeda')}.
      </Typography>
    </Stack>
  );
};

export default TelaResultado;
